import { io } from "socket.io-client";

const LOG = "[CardUpload:Socket]";

let socketInstance = null;

/**
 * Centralized step-by-step logger for all socket lifecycle/events.
 * Keeps a consistent format: [CardUpload:Socket] <event> <data>
 */
function logSocketEvent(event, data) {
  if (data !== undefined) {
    console.log(LOG, event, data);
  } else {
    console.log(LOG, event);
  }
}

export function getSocketBaseUrl(apiBaseUrl) {
  const explicit = import.meta.env.VITE_SOCKET_URL;
  if (explicit) return String(explicit).replace(/\/$/, "");
  try {
    const url = new URL(apiBaseUrl);
    return `${url.protocol}//${url.host}`;
  } catch {
    return String(apiBaseUrl).replace(/\/$/, "");
  }
}

export function connectBatchSocket(apiBaseUrl) {
  const token = localStorage.getItem("access");
  if (!token) {
    console.warn(LOG, "No access token in localStorage");
    return null;
  }

  const url = getSocketBaseUrl(apiBaseUrl);

  if (!socketInstance) {
    logSocketEvent("Connecting", { url });
    socketInstance = io(url, {
      path: "/socket.io/",
      transports: ["websocket"],
      autoConnect: true,
      auth: { token },
    });
    socketInstance.on("connect", () => {
      logSocketEvent("Socket connected", { socketId: socketInstance.id });
    });
    socketInstance.on("disconnect", (reason) => {
      logSocketEvent("Socket disconnected", { reason });
    });
    socketInstance.on("connect_error", (err) => {
      console.error(LOG, "Socket connect_error", err?.message || err);
    });
  } else {
    logSocketEvent("Reusing existing socket", { connected: socketInstance.connected });
    socketInstance.auth = { token };
    if (!socketInstance.connected) {
      socketInstance.connect();
    }
  }

  return socketInstance;
}

/**
 * Subscribe to batch.upload.progress events for a specific batch.
 * The server auto-joins the socket to user_<user_id>; events are filtered by batch_id.
 */
export function subscribeBatchUploadProgress(batchId, handlers, apiBaseUrl) {
  const socket = connectBatchSocket(apiBaseUrl);
  if (!socket) return () => {};

  const normalizedId = String(batchId);

  const onProgress = (payload) => {
    if (!payload || typeof payload !== "object") return;

    const payloadBatchId = payload.batch_id ?? payload.batchId;
    if (
      payloadBatchId != null &&
      String(payloadBatchId) !== normalizedId
    ) {
      return;
    }

    logSocketEvent("batch.upload.progress", payload);

    const status = String(payload.status || "").toLowerCase();

    if (status === "completed") {
      logSocketEvent("batch.upload.progress -> completed", payload);
      handlers.onComplete?.(payload);
      return;
    }
    if (status === "failed") {
      logSocketEvent("batch.upload.progress -> failed", payload);
      handlers.onFailed?.(payload);
      return;
    }

    handlers.onProgress?.(payload);
  };

  logSocketEvent("Subscribing batch.upload.progress", { batchId: normalizedId });
  socket.on("batch.upload.progress", onProgress);

  return () => {
    logSocketEvent("Unsubscribing batch.upload.progress", { batchId: normalizedId });
    socket.off("batch.upload.progress", onProgress);
  };
}

/**
 * Subscribe to batch.ocr.progress events for a specific batch.
 * The server auto-joins the socket to user_<user_id>; events are filtered by batch_id.
 */
export function subscribeBatchOcrProgress(batchId, handlers, apiBaseUrl) {
  const socket = connectBatchSocket(apiBaseUrl);
  if (!socket) return () => {};

  const normalizedId = String(batchId);

  const onProgress = (payload) => {
    if (!payload || typeof payload !== "object") return;

    const payloadBatchId = payload.batch_id ?? payload.batchId;
    if (
      payloadBatchId != null &&
      String(payloadBatchId) !== normalizedId
    ) {
      return;
    }

    logSocketEvent("batch.ocr.progress", payload);

    handlers.onProgress?.(payload);

    if (payload.is_complete) {
      logSocketEvent("batch.ocr.progress -> complete", payload);
      handlers.onComplete?.(payload);
    }
  };

  logSocketEvent("Subscribing batch.ocr.progress", { batchId: normalizedId });
  socket.on("batch.ocr.progress", onProgress);

  return () => {
    logSocketEvent("Unsubscribing batch.ocr.progress", { batchId: normalizedId });
    socket.off("batch.ocr.progress", onProgress);
  };
}

/**
 * Subscribe to ebay.sync.progress events.
 * Optionally filter by sync_run_id and/or batch_id.
 */
export function subscribeEbaySyncProgress(
  { syncRunId, batchId, handlers },
  apiBaseUrl
) {
  const socket = connectBatchSocket(apiBaseUrl);
  if (!socket) return () => {};

  const normalizedRunId =
    syncRunId != null ? String(syncRunId) : null;
  const normalizedBatchId =
    batchId != null ? String(batchId) : null;

  const onProgress = (payload) => {
    if (!payload || typeof payload !== "object") return;

    if (
      normalizedRunId != null &&
      String(payload.sync_run_id) !== normalizedRunId
    ) {
      return;
    }

    if (
      normalizedBatchId != null &&
      payload.batch_id != null &&
      String(payload.batch_id) !== normalizedBatchId
    ) {
      return;
    }

    logSocketEvent("ebay.sync.progress", payload);

    handlers.onProgress?.(payload);

    if (payload.completed) {
      logSocketEvent("ebay.sync.progress -> completed", payload);
      handlers.onComplete?.(payload);
    }
  };

  logSocketEvent("Subscribing ebay.sync.progress", {
    syncRunId: normalizedRunId,
    batchId: normalizedBatchId,
  });
  socket.on("ebay.sync.progress", onProgress);

  return () => {
    logSocketEvent("Unsubscribing ebay.sync.progress", {
      syncRunId: normalizedRunId,
      batchId: normalizedBatchId,
    });
    socket.off("ebay.sync.progress", onProgress);
  };
}

/**
 * Subscribe to ebay.price_quantity_update.progress events for a specific
 * PriceQuantityUpdateRun (triggered by editing quantity/price on a listed card).
 */
export function subscribeEbayPriceQuantityUpdateProgress(
  { updateRunId, handlers },
  apiBaseUrl
) {
  const PQ_LOG = "[eBay:PriceQty]";
  const socket = connectBatchSocket(apiBaseUrl);
  if (!socket) {
    console.warn(PQ_LOG, "4. No socket available, cannot subscribe to progress");
    return () => {};
  }

  const normalizedRunId = updateRunId != null ? String(updateRunId) : null;

  const onProgress = (payload) => {
    if (!payload || typeof payload !== "object") return;

    if (
      normalizedRunId != null &&
      String(payload.update_run_id) !== normalizedRunId
    ) {
      // Event for a different update run - ignore
      return;
    }

    console.log(PQ_LOG, "5. Progress event received", payload);
    logSocketEvent("ebay.price_quantity_update.progress", payload);

    handlers.onProgress?.(payload);

    if (payload.completed) {
      console.log(PQ_LOG, "6. Update run completed", {
        update_run_id: payload.update_run_id,
        succeeded: payload.succeeded,
        failed: payload.failed,
        skipped: payload.skipped,
        total: payload.total,
        status: payload.status,
      });
      logSocketEvent("ebay.price_quantity_update.progress -> completed", payload);
      handlers.onComplete?.(payload);
    }
  };

  console.log(PQ_LOG, "4. Subscribing to ebay.price_quantity_update.progress", {
    updateRunId: normalizedRunId,
  });
  logSocketEvent("Subscribing ebay.price_quantity_update.progress", {
    updateRunId: normalizedRunId,
  });
  socket.on("ebay.price_quantity_update.progress", onProgress);

  return () => {
    console.log(PQ_LOG, "Unsubscribing from ebay.price_quantity_update.progress", {
      updateRunId: normalizedRunId,
    });
    logSocketEvent("Unsubscribing ebay.price_quantity_update.progress", {
      updateRunId: normalizedRunId,
    });
    socket.off("ebay.price_quantity_update.progress", onProgress);
  };
}
